import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from '@headlessui/react'
import { NavLink, useNavigate } from 'react-router'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import { getAuth, signOut } from 'firebase/auth'
import { firebaseApp } from '../../main'
import { useAppAuth } from '../../app/hooks/UseAppAuth.ts'
import TopNavSkeleton from '../skeletons/TopNavSkeleton.tsx'
import { useEffect, useState } from 'react'
import logo from '../../assets/logo.svg'

interface MenuItem {
  id: number
  name: string
  link: string
  shouldBeLoggedIn: boolean
}

const allMenuItems: MenuItem[] = [
  {
    id: 0,
    name: 'Club Home',
    link: `/club/:clubId`,
    shouldBeLoggedIn: true,
  },
  {
    id: 1,
    name: 'Available Clubs',
    link: '/clubs',
    shouldBeLoggedIn: false,
  },
  {
    id: 2,
    name: 'Register Club',
    link: '/register-club',
    shouldBeLoggedIn: false,
  },
]

export default function TopNav() {
  const auth = getAuth(firebaseApp)
  const navigate = useNavigate()

  const { isAuthenticated, user, userLoaded } = useAppAuth()
  const [username, setUsername] = useState<string>('Login')
  const [menuItems, setMenuItems] = useState<MenuItem[]>([])

  useEffect(() => {
    setUsername(isAuthenticated ? (user?.name ?? 'Login') : 'Login')

    const defaultClubId = '500'
    const currentClubId = isAuthenticated
      ? (user?.clubs[0]?.id.toString() ?? defaultClubId)
      : defaultClubId

    const updatedMenuItems = allMenuItems.map((item) => {
      if (item.link.includes(':clubId')) {
        return { ...item, link: item.link.replace(':clubId', currentClubId) }
      }
      return item
    })

    setMenuItems(updatedMenuItems)
  }, [isAuthenticated, user])

  if (!userLoaded) {
    return <TopNavSkeleton />
  }

  const performLogout = async () => {
    await signOut(auth)
    navigate('/')
  }

  return (
    <Disclosure as="nav" className="bg-white shadow-sm">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 custom-max-w-920px">
        <div className="relative flex h-16 justify-between">
          <div className="flex">
            <NavLink to="/" className="flex flex-shrink-0 items-center">
              <img
                className="block h-8 w-auto sm:hidden"
                src={logo}
                alt="Speech Agenda"
              />
              <img
                className="hidden h-8 w-auto sm:block mt-1.5"
                src={logo}
                alt="Speech Agenda"
              />
            </NavLink>
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              {/* Current: "border-indigo-500 text-gray-900", Default: "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700" */}
              {menuItems
                .filter((item) => {
                  if (!isAuthenticated) {
                    return item.shouldBeLoggedIn === isAuthenticated
                  }
                  return item
                })
                .map((item) => (
                  <NavLink
                    key={item.id}
                    to={item.link}
                    className={({ isActive }: { isActive: boolean }) =>
                      'inline-flex items-center px-1 pt-1 text-sm font-medium' +
                      (isActive
                        ? ' border-b-2 border-indigo-500 text-gray-900'
                        : ' text-gray-500 hover:border-gray-300 hover:text-gray-700')
                    }>
                    {item.name}
                  </NavLink>
                ))}
            </div>
          </div>
          <div className="absolute inset-y-0 right-0 items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0 hidden sm:flex">
            {/* Profile dropdown For Desktop*/}
            {!isAuthenticated && (
              <NavLink
                to="/login"
                className="relative flex rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                Login
              </NavLink>
            )}
            {isAuthenticated && (
              <Menu as="div" className="relative ml-3">
                <div>
                  <MenuButton className="relative flex rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                    <span className="sr-only">Open user menu</span>
                    <span className="absolute -inset-1.5" />
                    <span>{username}</span>
                    <span style={{ marginTop: '5px', marginLeft: '0' }}>
                      <svg
                        aria-hidden="true"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="ml-3 h-3 w-3 stroke-slate-400">
                        <path
                          d="M9.75 4.125 6 7.875l-3.75-3.75"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"></path>
                      </svg>
                    </span>
                  </MenuButton>
                </div>
                <Transition
                  enter="transition duration-100 ease-out"
                  enterFrom="transform scale-95 opacity-0"
                  enterTo="transform scale-100 opacity-100"
                  leave="transition duration-75 ease-out"
                  leaveFrom="transform scale-100 opacity-100"
                  leaveTo="transform scale-95 opacity-0">
                  <MenuItems
                    transition
                    className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 ring-1 shadow-lg ring-black/5 transition focus:outline-hidden data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-200 data-enter:ease-out data-leave:duration-75 data-leave:ease-in">
                    <MenuItem>
                      <NavLink
                        to="/profile"
                        className="block px-4 py-2 text-sm text-gray-700 data-focus:bg-gray-100 data-focus:outline-hidden">
                        Profile
                      </NavLink>
                    </MenuItem>
                    <MenuItem>
                      <button
                        onClick={performLogout}
                        className="flex w-full px-4 py-2 text-sm text-gray-700 data-focus:bg-gray-100 data-focus:outline-hidden">
                        Sign out
                      </button>
                    </MenuItem>
                  </MenuItems>
                </Transition>
              </Menu>
            )}
          </div>
          <div className="-mr-2 flex items-center sm:hidden">
            {/* Mobile menu button */}
            <DisclosureButton className="group relative inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:ring-2 focus:ring-indigo-500 focus:outline-hidden focus:ring-inset">
              <span className="absolute -inset-0.5" />
              <span className="sr-only">Open main menu</span>
              <Bars3Icon
                aria-hidden="true"
                className="block size-6 group-data-open:hidden"
              />
              <XMarkIcon
                aria-hidden="true"
                className="hidden size-6 group-data-open:block"
              />
            </DisclosureButton>
          </div>
        </div>
      </div>
      <DisclosurePanel className="sm:hidden">
        <div className="space-y-1 pt-2 pb-4">
          {menuItems
            .filter((item) => {
              if (!isAuthenticated) {
                return item.shouldBeLoggedIn === isAuthenticated
              }
              return item
            })
            .map((item) => (
              <DisclosureButton
                key={item.id}
                as="div"
                className="block border-l-4 border-transparent py-2 pr-4 pl-3 text-base font-medium text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700">
                <NavLink to={item.link} className="block">
                  {item.name}
                </NavLink>
              </DisclosureButton>
            ))}
        </div>
        <div className="border-t border-gray-200 pb-3 pt-4">
          {/* Current: "bg-indigo-50 border-indigo-500 text-indigo-700", Default: "border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700" */}
          {isAuthenticated && (
            <>
              <DisclosureButton
                as="div"
                className="block border-l-4 border-transparent py-2 pr-4 pl-3 text-base font-medium text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700">
                <NavLink to="/profile" className="block">
                  Profile
                </NavLink>
              </DisclosureButton>
              <DisclosureButton
                as="div"
                className="block border-l-4 border-transparent py-2 pr-4 pl-3 text-base font-medium text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700">
                <button onClick={performLogout} className="w-full flex">
                  Sign out
                </button>
              </DisclosureButton>
            </>
          )}
          {!isAuthenticated && (
            <>
              <DisclosureButton
                as="div"
                className="block border-l-4 border-transparent py-2 pr-4 pl-3 text-base font-medium text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700">
                <NavLink to="/login" className="block">
                  Login
                </NavLink>
              </DisclosureButton>
            </>
          )}
        </div>
      </DisclosurePanel>
    </Disclosure>
  )
}
